import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import OwnerLayout from "../../../layout/OwnerLayout";
import { getScreensByOwner } from "../../../../api/screen.api";
import { getCurrentUser } from "../../../../api/user.api";
import { UserContext } from "../../../../context/UserContext";
import { RotatingLines } from "react-loader-spinner";

function Screens() {
  const [screensData, setScreensData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);

  const fetchScreens = () => {
    setLoading(true);
    getCurrentUser()
      .then((res) => {
        getScreensByOwner(res?.data?.user?.ownerId)
          .then((res) => {
            setScreensData(res?.data?.screens);
            setLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching screens:", error);
            setLoading(false);
          });
      })
      .catch((error) => {
        console.error("Error fetching user:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchScreens();
  }, []);

  // Function to truncate long text with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "-";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Function to render location based on screen type
  const renderLocation = (screen) => {
    if (screen.screenType === "restaurant" && screen.restaurant) {
      return screen.restaurant.location || "-";
    } else if (screen.screenType === "vehicle" && screen.vehicle) {
      return screen.vehicle.location || "-";
    }
    return "-";
  };

  return (
    <OwnerLayout title="Screen Management" ownerDetails={user}>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : (
        <div className="mx-2 sm:mx-4 lg:mx-8">
          {/* Empty state */}
          {screensData.length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-500 text-lg">No screens found</p>
            </div>
          )}

          {/* Screens Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {screensData.map((screen, index) => (
              <Link
                to={`/screendetails/${screen._id}`}
                key={screen._id || index}
                className="cursor-pointer transition-all duration-300 hover:shadow-lg"
              >
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col border border-gray-100">
                  {/* Header with model */}
                  <div className="px-4 py-3 flex justify-between items-center bg-yellow">
                    <h3 className="font-semibold text-gray-800">
                      {truncateText(screen?.model, 25)}
                    </h3>
                  </div>

                  {/* Main content */}
                  <div className="p-4 flex-1">
                    {/* Status indicators */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Screen Status</p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            screen?.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : screen?.status === "In Active"
                              ? "bg-orange-100 text-orange-800"
                              : screen?.status === "Under Maintenance"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {screen?.status || "Unknown"}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Display Status</p>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            screen?.onlineStatus === "online"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {screen?.onlineStatus || "offline"}
                        </span>
                      </div>
                    </div>

                    {/* Type and Model */}
                    <div className="space-y-2 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Screen Type</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">
                          {screen?.screenType || "-"}
                        </p>
                      </div>

                      {screen.screenType === "restaurant" &&
                        screen.restaurant && (
                          <div>
                            <p className="text-xs text-gray-500">
                              Fixed Location
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {truncateText(screen.restaurant.name, 30)}
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Dynamic Details based on screen type */}
                    <div className="border-t border-gray-100 pt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {screen.screenType === "restaurant"
                          ? "Fixed Location Details"
                          : "Mobile Location Details"}
                      </h4>
                      <div className="space-y-2">
                        {screen.screenType === "restaurant" ? (
                          // Restaurant Details
                          <>
                            <div>
                              <p className="text-xs text-gray-500">Name</p>
                              <p className="text-sm font-medium text-gray-900 break-words">
                                {truncateText(screen.restaurant?.name, 30) ||
                                  "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Location</p>
                              <p className="text-sm font-medium text-gray-900 break-words">
                                {truncateText(
                                  screen.restaurant?.location,
                                  30
                                ) || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Cuisine</p>
                              <p className="text-sm font-medium text-gray-900 break-words">
                                {truncateText(
                                  screen.restaurant?.cuisineType,
                                  30
                                ) || "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Capacity</p>
                              <p className="text-sm font-medium text-gray-900 break-words">
                                {screen.restaurant?.seatingCapacity || "-"}
                              </p>
                            </div>
                          </>
                        ) : (
                          // Vehicle Details
                          <>
                            {[
                              { label: "Brand", value: screen?.vehicle?.brand },
                              { label: "Model", value: screen?.vehicle?.model },
                              { label: "Type", value: screen?.vehicle?.type },
                              {
                                label: "Location",
                                value: screen?.vehicle?.location,
                              },
                            ].map((item, i) => (
                              <div key={i}>
                                <p className="text-xs text-gray-500">
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {truncateText(item.value, 30) || "-"}
                                </p>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}

export default Screens;
